// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import authService from "../Services/authService";

/**
 * @typedef {import('../types/auth').AuthUser} AuthUser
 * @typedef {import('../types/auth').Permission} Permission
 */

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => authService.getUser());
  const [isLoading, setIsLoading] = useState(() => !authService.isSessionChecked());

  // Restore session from /api/me on initial page load / refresh
  const hydrateSession = useCallback(async (force = false) => {
    try {
      const result = await authService.checkSession(force);
      if (result?.user) {
        setUser(result.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Skip session hydration on the login page — there's no valid session
    // to restore, and calling /api/me would trigger a 401 → refresh → 401 loop.
    const isLoginPage = window.location.pathname === "/login" || window.location.pathname === "/";

    if (!isLoginPage && !authService.isSessionChecked()) {
      hydrateSession();
    } else {
      setIsLoading(false);
    }

    // Listen for auth state changes from authService (login, external logout, multi-tab sync)
    const handleUserChanged = (e) => {
      if (isMounted) {
        setUser(e.detail?.user || null);
        setIsLoading(false);
      }
    };

    // Listen for 403 Forbidden events
    const handleForbidden = (e) => {
      console.warn("[RBAC AuthContext] 403 Forbidden:", e.detail?.message);
    };

    window.addEventListener("auth:user_changed", handleUserChanged);
    window.addEventListener("auth:forbidden", handleForbidden);

    return () => {
      isMounted = false;
      window.removeEventListener("auth:user_changed", handleUserChanged);
      window.removeEventListener("auth:forbidden", handleForbidden);
    };
  }, [hydrateSession]);

  const login = useCallback(async (credentials, maybePassword) => {
    setIsLoading(true);
    try {
      const email = typeof credentials === "object" ? credentials?.email : credentials;
      const password = typeof credentials === "object" ? credentials?.password : maybePassword;
      const response = await authService.login(email, password);
      if (response?.data) {
        setUser(response.data);
      }
      return response;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (reason = "user_action") => {
    setIsLoading(true);
    try {
      await authService.logout(reason);
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const can = useCallback(
    (permission) => {
      return authService.can(permission);
    },
    // Dependency on user ensures re-evaluation when auth changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const canAny = useCallback(
    (...permissions) => {
      return authService.canAny(...permissions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const canAll = useCallback(
    (...permissions) => {
      return authService.canAll(...permissions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const hasRole = useCallback(
    (...roleIds) => {
      return authService.hasRole(...roleIds);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      loading: isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      checkSession: hydrateSession,
      can,
      canAny,
      canAll,
      hasRole,
    }),
    [user, isLoading, login, logout, hydrateSession, can, canAny, canAll, hasRole]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
